import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { animations } from '../design-system/tokens/animations';
import { breakpoints } from '../design-system/tokens/breakpoints';

const ToggleContainer = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: ${spacing[2]};
  min-width: 44px;
  min-height: 44px;
  padding: ${spacing[2]} ${spacing[3]};
  background: ${props => props.theme.colors.surface};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  cursor: pointer;
  transition: ${animations.transition(
    ['background-color', 'border-color', 'transform'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  @media (max-width: ${breakpoints.lg}) {
    padding: ${spacing[2]};
  }

  &:hover {
    background: ${props => props.theme.colors.surfaceHover};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const IconContainer = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  svg {
    width: 100%;
    height: 100%;
    stroke: ${props => props.theme.colors.text};
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const Label = styled.span`
  @media (max-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const SunIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="4" />
    <line x1="12" y1="2" x2="12" y2="4" />
    <line x1="12" y1="20" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
    <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="4" y2="12" />
    <line x1="20" y1="12" x2="22" y2="12" />
    <line x1="6.34" y1="17.66" x2="4.93" y2="19.07" />
    <line x1="19.07" y1="4.93" x2="17.66" y2="6.34" />
  </svg>
);

const MoonIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const ThemeToggle: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const label =
    theme.mode === 'light' ? t('theme.dark') : t('theme.light');

  return (
    <ToggleContainer
      type="button"
      onClick={theme.toggleTheme}
      title={t('theme.toggle')}
      aria-label={t('theme.toggle')}
    >
      <IconContainer>
        {theme.mode === 'light' ? <MoonIcon /> : <SunIcon />}
      </IconContainer>
      <Label>{label}</Label>
    </ToggleContainer>
  );
};

export default ThemeToggle;
